import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { Link as RouterLink } from "react-router-dom";
import { generateLabels } from "../platform/labelGenerator";
import { SectionCard } from "./ui/PlatformPrimitives";
import Label3DPreview from "./Label3DPreview";
import { Pictogram, PICTOGRAM_INDEX } from "./LabelPictograms";

const LANG_NAMES = { en: "English", de: "Deutsch", fr: "Français", it: "Italiano", es: "Español", pt: "Português", nl: "Nederlands" };

// Real cosmetic labels use a condensed sans-serif so dense INCI lines fit on
// the back panel. Roboto Condensed and Archivo Narrow are the closest
// open-source matches to the Helvetica/Univers/Frutiger Condensed families
// used on commercial labels. Falls back to system condensed forms.
const LABEL_FONT =
  '"Roboto Condensed", "Archivo Narrow", "Helvetica Neue Condensed", "Helvetica Condensed", "Arial Narrow", Arial, sans-serif';

const downloadJson = (filename, payload) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const downloadText = (filename, content) => {
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const labelToText = (label) => {
  const lines = [`# ${label.jurisdiction} label draft`, ""];
  Object.entries(label.panels).forEach(([key, panel]) => {
    lines.push(`## ${panel.heading}`);
    panel.lines.forEach((line) => {
      if (line.text) lines.push(`  ${line.uppercase ? line.text.toUpperCase() : line.text}`);
    });
    lines.push("");
  });
  if (label.warnings?.length) {
    lines.push("## Warnings");
    label.warnings.forEach((w) => lines.push(`  • ${w.text} (${w.citation})`));
    lines.push("");
  }
  if (label.annexWarnings?.length) {
    lines.push("## EU Annex III warnings");
    label.annexWarnings.forEach((w) => lines.push(`  • ${w.text} — ${w.substance}`));
    lines.push("");
  }
  lines.push("## Citations");
  label.citations.forEach((c) => lines.push(`  • ${c.regulation} ${c.article} (${c.url})`));
  return lines.join("\n");
};

const PaoSymbol = ({ months }) => <Pictogram kind="pao" size={36} value={months} />;
const HourglassSymbol = () => <Pictogram kind="hourglass" size={28} />;
const OpenBookSymbol = () => <Pictogram kind="open_book" size={32} />;

// IMPORTANT: the flat label panels in LabelPanelLayout represent a *physical
// printed sticker*. They must STAY solid white — they're not part of the
// app chrome. Because we use a plain `<Box>` here (not Paper/Card) we
// already escape the theme's default-glass treatment automatically. The
// rectangle below is intentionally bgcolor:#FFFFFF with a hairline border
// and squared corners (borderRadius: 0) so the proof reads like a print
// artifact, not frosted glass.
const Panel = ({ panel }) => (
  <Box
    sx={{
      bgcolor: "#FFFFFF",
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: 0,
      p: 2.5,
      minHeight: 200,
      display: "flex",
      flexDirection: "column",
      fontFamily: LABEL_FONT,
      // Defensive: explicitly reset backdrop-filter in case a future theme
      // change ever leaks it onto Box children.
      backdropFilter: "none",
      WebkitBackdropFilter: "none",
    }}
  >
    <Typography
      sx={{
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontSize: "0.55rem",
        fontWeight: 600,
        color: "#9CA3AF",
        mb: 1.5,
        fontFamily: "inherit",
      }}
    >
      {panel.heading}
    </Typography>
    <Stack spacing={0.5} sx={{ flex: 1 }}>
      {panel.lines.map((line) => {
        const isMissing = line.required && (!line.text || line.text === "—");
        // Tighten letter-spacing on the dense ingredient line, like real labels.
        const isIngredients = line.key === "ingredients";
        return (
          <Stack
            key={line.key}
            direction="row"
            spacing={1}
            alignItems="flex-start"
            sx={{ mt: line.mt ? line.mt : 0 }}
          >
            {line.symbol === "pao" && <PaoSymbol months={line.text} />}
            {line.symbol === "hourglass" && <HourglassSymbol />}
            {line.symbol === "open_book" && <OpenBookSymbol />}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                component="span"
                sx={{
                  fontFamily: "inherit",
                  fontSize: line.fontSize || "0.75rem",
                  fontWeight: line.weight || 400,
                  color: isMissing ? "#991B1B" : line.color || "#111827",
                  textTransform: line.uppercase ? "uppercase" : "none",
                  letterSpacing: line.uppercase ? "0.08em" : isIngredients ? "0.005em" : "0.01em",
                  lineHeight: line.lineHeight || 1.4,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {line.text || (isMissing ? "[ missing ]" : "")}
              </Typography>
              {line.note && (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "0.55rem",
                    color: "#9CA3AF",
                    ml: 0.75,
                    fontStyle: "italic",
                  }}
                >
                  {line.note}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  </Box>
);

const RequiredFieldRow = ({ field, productId }) => {
  const fixUrl = !field.present && productId ? `/perfume-dashboard?product=${productId}` : null;
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1, minHeight: 36 }}>
      {field.present ? (
        // Allow-listed severity contrast token.
        <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16, color: "#2D6A4F", flexShrink: 0 }} />
      ) : (
        <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: "#991B1B", flexShrink: 0 }} />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          // Missing fields use error-contrast token; present rows defer to theme.
          color={field.present ? "text.primary" : "error.main"}
          sx={{ fontWeight: field.present ? 500 : 600 }}
        >
          {field.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {field.citation}
        </Typography>
      </Box>
      {fixUrl && (
        <Button
          size="small"
          variant="outlined"
          component={RouterLink}
          to={fixUrl}
          sx={{ height: 28, px: 1.5, minWidth: 0, borderRadius: 999 }}
        >
          Fix →
        </Button>
      )}
      {field.citationUrl && (
        <Link
          href={field.citationUrl}
          target="_blank"
          rel="noopener noreferrer"
          // Navy brand mark text (#1B2A4A) is allow-listed for accent links.
          sx={{ color: "#1B2A4A", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.5, typography: "caption" }}
        >
          Source <OpenInNewRoundedIcon sx={{ fontSize: 12 }} />
        </Link>
      )}
    </Stack>
  );
};

const LabelPanelLayout = ({ label }) => {
  if (label.jurisdiction === "EU") {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Panel panel={label.panels.front} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Panel panel={label.panels.information} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Panel panel={label.panels.legal} />
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Panel panel={label.panels.pdp} />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Panel panel={label.panels.info} />
      </Grid>
    </Grid>
  );
};

const groupWarningsBySource = (warnings = []) => {
  const groups = { annex: [], fda: [], state: [], other: [] };
  warnings.forEach((w) => {
    const code = String(w.code || "");
    if (code.startsWith("CA_") || code.startsWith("WA_") || code.startsWith("NY_") || code.startsWith("MD_")) {
      groups.state.push(w);
    } else if (code.startsWith("EU_ANNEX") || w.referenceNumber) {
      groups.annex.push(w);
    } else if (code.startsWith("FDA_") || code.startsWith("US_MOCRA") || code === "TALC_TESTING_DISCLOSURE" || code === "PFAS_DISCLOSURE" || code === "PROFESSIONAL_USE_STATEMENT") {
      groups.fda.push(w);
    } else {
      groups.other.push(w);
    }
  });
  return groups;
};

const copyToClipboard = (text) => {
  if (typeof navigator !== "undefined" && navigator.clipboard) {
    navigator.clipboard.writeText(text);
  }
};

const LabelView = ({ label, slugBase, productId }) => {
  const theme = useTheme();
  // Severity tints — translucent rgba over theme glass so banner reads as a
  // state without breaking the Liquid Glass surface beneath.
  const readyTint = "rgba(45,106,79,0.14)";
  const readyRing = "rgba(45,106,79,0.22)";
  const blockedTint = "rgba(153,27,27,0.14)";
  const blockedRing = "rgba(153,27,27,0.22)";
  return (
  <Stack spacing={2}>
    {/* Status banner — glass pill row. The severity tint sits on top of the
        translucent Paper as a soft overlay so the banner reads as a state
        without breaking the Liquid Glass look. */}
    <Paper
      sx={{
        px: 3,
        py: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
        borderRadius: 999, // pill
        // Hairline translucent ring — no sharp hex borders.
        border: `1px solid ${label.isReady ? readyRing : blockedRing}`,
        // Severity tint overlay — additive, sits on top of the theme glass.
        backgroundImage: `linear-gradient(${label.isReady ? readyTint : blockedTint}, ${label.isReady ? readyTint : blockedTint})`,
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: 999, bgcolor: label.isReady ? "success.main" : "error.main", position: "relative", zIndex: 1 }} />
      <Typography
        variant="body2"
        // Severity contrast text — allow-listed #2D6A4F / #991B1B tokens.
        sx={{ color: label.isReady ? "#2D6A4F" : "#991B1B", fontWeight: 600, flex: 1, position: "relative", zIndex: 1 }}
      >
        {label.isReady
          ? `${label.jurisdiction} label ready — every required field is present.`
          : `${label.jurisdiction} label not ready — ${label.missingFields.length} required field${label.missingFields.length === 1 ? "" : "s"} missing.`}
      </Typography>
      <Stack direction="row" spacing={1} className="label-actions" sx={{ position: "relative", zIndex: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentCopyRoundedIcon sx={{ fontSize: 14 }} />}
          onClick={() =>
            copyToClipboard(
              (label.ingredientList || [])
                .map((i) => i.inciName)
                .join(", ")
            )
          }
        >
          Copy INCI
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PrintRoundedIcon sx={{ fontSize: 14 }} />}
          onClick={() => window.print()}
        >
          Print
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={() => downloadText(`${slugBase}-${label.jurisdiction.toLowerCase()}-label.txt`, labelToText(label))}
        >
          TXT
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={() => downloadJson(`${slugBase}-${label.jurisdiction.toLowerCase()}-label.json`, label)}
        >
          JSON
        </Button>
      </Stack>
    </Paper>

    {/* Flat panels — primary review surface, visible above the fold */}
    <Box className="label-print-area">
      <LabelPanelLayout label={label} />
    </Box>

    {/* Required fields checklist — second-most-important content */}
    <SectionCard title={`Required fields · ${label.requiredFields.filter((f) => f.present).length}/${label.requiredFields.length}`}>
      <Stack spacing={1}>
        {label.requiredFields.map((field) => (
          <RequiredFieldRow key={field.key} field={field} productId={productId} />
        ))}
      </Stack>
    </SectionCard>

    {/* 3D rotatable package — secondary shape verification */}
    <SectionCard title="3D package preview">
      <Label3DPreview label={label} />
    </SectionCard>

    {/* Warnings — grouped by source */}
    {(() => {
      const groups = groupWarningsBySource(label.warnings || []);
      const totalWarnings =
        (label.annexWarnings || []).length +
        groups.fda.length +
        groups.state.length +
        groups.other.length;
      if (!totalWarnings) return null;
      const renderGroup = (title, items, palette, formatItem) => {
        if (!items.length) return null;
        return (
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {title} · {items.length}
            </Typography>
            <Stack spacing={1.5}>
              {items.map((item, i) => (
                // Warning rows are glass with a severity tint overlay — same
                // pattern as the status banner so all callouts on this page
                // read as one design family. Surface-tier radius 16 (theme
                // default) keeps it concentric with the SectionCard.
                <Paper
                  key={`${title}-${i}`}
                  sx={{
                    p: 2,
                    backgroundImage: `linear-gradient(${palette.tint}, ${palette.tint})`,
                    border: `1px solid ${palette.border}`,
                  }}
                >
                  <Box sx={{ position: "relative", zIndex: 1 }}>{formatItem(item)}</Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        );
      };
      return (
        <SectionCard title={`Mandatory warnings · ${totalWarnings}`}>
          <Stack spacing={2}>
            {renderGroup(
              "EU Annex III (per-substance)",
              label.annexWarnings || [],
              { tint: "rgba(146,64,14,0.14)", border: "rgba(146,64,14,0.22)" },
              (w) => (
                <>
                  <Typography variant="body2" sx={{ color: "#92400E", fontWeight: 600 }}>
                    {w.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    {w.substance} — Annex III §{w.referenceNumber}
                    {w.sourceUrl && (
                      <Link href={w.sourceUrl} target="_blank" rel="noopener noreferrer" sx={{ ml: 1, typography: "caption" }}>
                        Source ↗
                      </Link>
                    )}
                  </Typography>
                </>
              )
            )}
            {renderGroup(
              "FDA / MoCRA",
              groups.fda,
              { tint: "rgba(153,27,27,0.14)", border: "rgba(153,27,27,0.22)" },
              (w) => (
                <>
                  <Typography variant="body2" sx={{ color: "#991B1B", fontWeight: 600 }}>
                    {w.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    {w.citation}
                    {w.citationUrl && (
                      <Link href={w.citationUrl} target="_blank" rel="noopener noreferrer" sx={{ ml: 1, typography: "caption" }}>
                        Source ↗
                      </Link>
                    )}
                  </Typography>
                </>
              )
            )}
            {renderGroup(
              "US state-level",
              groups.state,
              { tint: "rgba(146,64,14,0.14)", border: "rgba(146,64,14,0.22)" },
              (w) => (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    {w.state && (
                      <Chip
                        label={w.state}
                        size="small"
                        // Navy brand-mark text (#1B2A4A) is allow-listed.
                        // Translucent inset chip — defers to theme glass.
                        sx={{ borderRadius: 999, color: "#1B2A4A", fontWeight: 700 }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" sx={{ color: w.blocking ? "#991B1B" : "#92400E", fontWeight: 600 }}>
                    {w.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    {w.citation}
                    {w.citationUrl && (
                      <Link href={w.citationUrl} target="_blank" rel="noopener noreferrer" sx={{ ml: 1, typography: "caption" }}>
                        Source ↗
                      </Link>
                    )}
                  </Typography>
                </>
              )
            )}
            {renderGroup(
              "Other",
              groups.other,
              { tint: "rgba(153,27,27,0.14)", border: "rgba(153,27,27,0.22)" },
              (w) => (
                <>
                  <Typography variant="body2" sx={{ color: "#991B1B", fontWeight: 600 }}>
                    {w.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    {w.citation}
                  </Typography>
                </>
              )
            )}
          </Stack>
        </SectionCard>
      );
    })()}

    {/* Pictograms — split into mandatory (per regulation) vs voluntary.
        Each card answers the 6 most common designer/regulatory questions
        without leaving the page:
          • What does it mean?      → label + citation
          • Why is it here?         → triggeredBy badge
          • Min print size?         → minSizeMm tooltip
          • Source verification?    → click → Wikimedia Commons
          • Hand-off to designer?   → download SVG button
          • Hide if irrelevant?     → "available but not active" hints below */}
    {label.pictograms && label.pictograms.length > 0 && (() => {
      const mandatory = label.pictograms.filter((p) => !p.optional);
      const voluntary = label.pictograms.filter((p) => p.optional);
      const activeKinds = new Set(label.pictograms.map((p) => p.kind));
      // EU is the superset; the "available but not active" hints only apply
      // there. US has no mandatory pictograms so the prompt would be noise.
      const availableHints =
        label.jurisdiction === "EU"
          ? [
              !activeKinds.has("open_book") && {
                kind: "open_book",
                hint: "Enable the small-package toggle on the product to add this.",
              },
              !activeKinds.has("triman") && {
                kind: "triman",
                hint: "Add 'FR' to product markets to add this (mandatory in France).",
              },
              !activeKinds.has("hourglass") &&
                !activeKinds.has("pao") && {
                  kind: "hourglass",
                  hint: "Set either PAO months or a date of minimum durability.",
                },
            ].filter(Boolean)
          : [];

      const PictogramCard = ({ p }) => {
        const meta = PICTOGRAM_INDEX[p.kind] || {};
        const tooltipBody = (
          <Box sx={{ p: 0.5, maxWidth: 280 }}>
            <Typography variant="caption" sx={{ display: "block", fontWeight: 600, mb: 0.5 }}>
              {meta.label || p.label}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.85)", lineHeight: 1.4 }}>
              {meta.citation || p.citation}
            </Typography>
            {p.triggeredBy && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "rgba(255,255,255,0.85)" }}>
                <strong>Trigger:</strong> {p.triggeredBy}
              </Typography>
            )}
            {meta.minSizeMm && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "rgba(255,255,255,0.85)" }}>
                <strong>Min print size:</strong> ≥ {meta.minSizeMm} mm
              </Typography>
            )}
            <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "rgba(255,255,255,0.65)", fontSize: "0.6rem" }}>
              Click → Wikimedia source · ⬇ icon downloads SVG
            </Typography>
          </Box>
        );

        const handleSourceClick = () => {
          if (meta.wikimediaUrl) {
            window.open(meta.wikimediaUrl, "_blank", "noopener,noreferrer");
          }
        };

        const handleDownload = (event) => {
          event.stopPropagation();
          if (!meta.assetUrl) return;
          const anchor = document.createElement("a");
          anchor.href = meta.assetUrl;
          anchor.download = `${p.kind}.svg`;
          anchor.click();
        };

        return (
          <Tooltip title={tooltipBody} arrow placement="top" enterDelay={250}>
            <Paper
              key={p.kind}
              role="button"
              tabIndex={0}
              onClick={handleSourceClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSourceClick();
                }
              }}
              sx={{
                p: 1.5,
                minWidth: 180,
                maxWidth: 240,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                // Glass-inset tier surface. Voluntary pictograms recede with a
                // lower-opacity background so the mandatory ones land first in
                // the visual hierarchy. Theme MuiPaper handles the radius (16)
                // + blur + specular ::before; we just tweak the background.
                background: p.optional ? theme.glass.inset.background : theme.glass.surface.background,
                cursor: meta.wikimediaUrl ? "pointer" : "default",
                // Liquid Glass spring motion — uses theme.motion.morph so the
                // press/hover feel matches the rest of the app's glass tier.
                transition: `border-color ${theme.motion.morph}, box-shadow ${theme.motion.morph}, transform ${theme.motion.press}`,
                "&:hover": {
                  // Translucent navy ring on hover — no sharp hex.
                  boxShadow: "inset 0 0 0 1.5px rgba(27,42,74,0.40), inset 0 1px 0 rgba(255,255,255,0.55), 0 4px 14px rgba(27,42,74,0.14)",
                  transform: "translateY(-1px)",
                },
                "&:focus-visible": {
                  outline: "none",
                  boxShadow: "inset 0 0 0 1.5px rgba(27,42,74,0.40)",
                },
              }}
            >
              <Box sx={{ flexShrink: 0, opacity: p.optional ? 0.7 : 1, position: "relative", zIndex: 1 }}>
                <Pictogram kind={p.kind} size={36} value={p.value} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1, position: "relative", zIndex: 1 }}>
                <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600, display: "block", lineHeight: 1.3 }}>
                  {meta.label || p.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {p.scope || meta.citation || p.citation}
                </Typography>
              </Box>
              {meta.assetUrl && (
                <Tooltip title="Download SVG" arrow>
                  <IconButton
                    size="small"
                    onClick={handleDownload}
                    aria-label={`Download ${meta.label || p.kind} SVG`}
                    sx={{ flexShrink: 0, position: "relative", zIndex: 1 }}
                  >
                    <DownloadRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Paper>
          </Tooltip>
        );
      };

      return (
        <Stack spacing={2}>
          {mandatory.length > 0 && (
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Mandatory pictograms · {label.jurisdiction}
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                {mandatory.map((p) => <PictogramCard key={p.kind} p={p} />)}
              </Stack>
            </Box>
          )}
          {voluntary.length > 0 && (
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Voluntary pictograms · {label.jurisdiction}
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                {voluntary.map((p) => <PictogramCard key={p.kind} p={p} />)}
              </Stack>
            </Box>
          )}
          {availableHints.length > 0 && (
            <Box component="details" sx={{ "& > summary": { cursor: "pointer", listStyle: "none" } }}>
              <Box component="summary">
                <Typography variant="caption" color="text.secondary">
                  + {availableHints.length} more available — how to enable
                </Typography>
              </Box>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {availableHints.map(({ kind, hint }) => {
                  const meta = PICTOGRAM_INDEX[kind] || {};
                  return (
                    <Stack key={kind} direction="row" spacing={1.5} alignItems="center" sx={{ opacity: 0.7 }}>
                      <Box sx={{ flexShrink: 0, filter: "grayscale(1)" }}>
                        <Pictogram kind={kind} size={20} />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>{meta.label || kind}</strong> — {hint}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Stack>
      );
    })()}

    {/* Net content + PDP geometry */}
    {(label.netContent || label.pdpGeometry) && (
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Net content + PDP geometry
        </Typography>
        <Paper sx={{ p: 2.5 }}>
          <Box sx={{ position: "relative", zIndex: 1 }}>
            {label.netContent && (
              <Typography variant="body2" color="text.primary" sx={{ display: "block" }}>
                <strong>Net content:</strong> {label.netContent.raw || "—"} ·{" "}
                {label.netContent.hasBoth ? (
                  // Allow-listed severity contrast tokens.
                  <Box component="span" sx={{ color: "#2D6A4F", fontWeight: 600 }}>dual-unit ✓</Box>
                ) : (
                  <Box component="span" sx={{ color: "#991B1B", fontWeight: 600 }}>
                    {label.netContent.hasMetric && !label.netContent.hasCustomary && "missing US customary"}
                    {!label.netContent.hasMetric && label.netContent.hasCustomary && "missing metric"}
                    {!label.netContent.hasMetric && !label.netContent.hasCustomary && "no recognised unit"}
                  </Box>
                )}
              </Typography>
            )}
            {label.pdpGeometry?.minLetterHeightIn && (
              <Typography variant="body2" color="text.primary" sx={{ display: "block", mt: 1 }}>
                <strong>PDP {label.pdpGeometry.pdpAreaSqIn} sq in</strong> · min letter height for net content:{" "}
                {label.pdpGeometry.minLetterHeightIn.toFixed(3)}″ ({label.pdpGeometry.minLetterHeightMm} mm)
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    )}

    {/* Citations */}
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Regulatory basis
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {label.citations.map((c, i) => (
          // Navy brand mark text + theme glass-inset chip surface.
          <Chip
            key={`${c.regulation}-${i}`}
            label={c.article}
            component={c.url ? Link : undefined}
            href={c.url}
            target="_blank"
            rel="noopener noreferrer"
            clickable={Boolean(c.url)}
            size="small"
            sx={{
              bgcolor: "rgba(27,42,74,0.10)",
              color: "#1B2A4A",
              borderRadius: 999,
              fontWeight: 600,
              textDecoration: "none",
              mb: 0.5,
            }}
          />
        ))}
      </Stack>
    </Box>
  </Stack>
  );
};

const LabelPreview = ({ product, compliance, regulatoryRules = [] }) => {
  const [tab, setTab] = useState("EU");
  const [language, setLanguage] = useState("en");
  const labels = useMemo(
    () => generateLabels({ product, compliance, regulatoryRules, languages: ["en", "de", "fr", "it", "es", "pt", "nl"] }),
    [product, compliance, regulatoryRules]
  );
  const slugBase = (product?.name || "product")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const active = tab === "EU" ? labels.EUByLanguage[language] || labels.EU : labels.US;

  return (
    <SectionCard
      title="Label drafts"
      action={
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {tab === "EU" && (
            <TextField
              size="small"
              select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              sx={{ minWidth: 130 }}
            >
              {Object.keys(LANG_NAMES).map((code) => (
                <MenuItem key={code} value={code}>
                  {LANG_NAMES[code]}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Chip
            size="small"
            color={labels.summary.euReady ? "success" : "error"}
            label={`EU ${labels.summary.euReady ? "✓" : `· ${labels.summary.euMissing} missing`}`}
            sx={{ borderRadius: 999 }}
          />
          <Chip
            size="small"
            color={labels.summary.usReady ? "success" : "error"}
            label={`US ${labels.summary.usReady ? "✓" : `· ${labels.summary.usMissing} missing`}`}
            sx={{ borderRadius: 999 }}
          />
          {labels.summary.stateFindings > 0 && (
            <Chip
              size="small"
              color="warning"
              label={`${labels.summary.stateFindings} state warning${labels.summary.stateFindings === 1 ? "" : "s"}`}
              sx={{ borderRadius: 999 }}
            />
          )}
        </Stack>
      }
    >
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="EU label" value="EU" />
        <Tab label="US label" value="US" />
      </Tabs>
      <LabelView label={active} slugBase={slugBase} productId={product?.id} />
    </SectionCard>
  );
};

export default LabelPreview;
